"use client";

import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { flushSync } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import Connectors from "./Connectors";
import TechPopup from "./TechPopup";
import ResponsiveCopy from "../../ui/ResponsiveCopy";

// Interactive "Built on Modern" diagram. The boxes are real <button>s laid out
// in the design's native 1033×450 coordinate space (positioned as %), sitting on
// top of the Connectors arrow layer. On a fine pointer the popup follows the
// cursor (tooltip); on touch a tap toggles an anchored popup; keyboard focus
// anchors it too. Below md the tree reflows to a readable stacked column.
const CARD_W = 340;
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export default function TechTree({ techs, closeLabel }) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState("hover"); // "hover" (cursor) | "anchor" (tap/focus)
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [anchor, setAnchor] = useState(null); // { cx, top, bottom }
  const [cardH, setCardH] = useState(240);
  // set for a scroll dismissal, which has to be gone by the next frame rather
  // than fade out while the page slides away underneath a fixed-position card
  const [instant, setInstant] = useState(false);
  const [win, setWin] = useState({ w: 1200, h: 800 });
  const cardRef = useRef(null);
  const hoverable = useRef(true);
  // Whether the open card came from the keyboard, which is the only case where
  // blurring the box should close it. Browsers differ on whether tapping a
  // <button> focuses it (Safari doesn't, Chrome does), so focus alone can't
  // stand in for "the user is navigating with a keyboard".
  const openedByKey = useRef(false);

  useEffect(() => {
    hoverable.current = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    ).matches;
    const onResize = () =>
      setWin({ w: window.innerWidth, h: window.innerHeight });
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Measure the card once it's shown so we can flip/clamp it accurately.
  useLayoutEffect(() => {
    if (active != null && cardRef.current) {
      setCardH(cardRef.current.offsetHeight);
    }
  }, [active]);

  // Dismiss an anchored (tap/focus) popup. Its position is a one-shot snapshot
  // of the box's rect in a fixed-position layer, so any scroll would leave it
  // floating detached from the box it describes — close it instead.
  useEffect(() => {
    if (active == null || mode !== "anchor") return;
    const close = () => setActive(null);
    // AnimatePresence animates the exit with the props the child had on its
    // last render as a present child, so `instant` has to be committed while
    // the card is still mounted. flushSync is what makes that ordering real:
    // scheduling the removal in a rAF instead let the frame beat React's flush
    // of the scroll handler, both states landed in one render, and the card
    // went on fading for the full 180ms.
    const closeNow = () => {
      flushSync(() => setInstant(true));
      setActive(null);
    };
    const onDown = (e) => {
      // taps inside the card (including its close button) are its own business
      if (
        e.target.closest("[data-tech-box]") ||
        e.target.closest("[data-tech-popup]")
      )
        return;
      close();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("scroll", closeNow, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("scroll", closeNow);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [active, mode]);

  const openHover = (i, e) => {
    setInstant(false);
    setActive(i);
    setMode("hover");
    setPos({ x: e.clientX, y: e.clientY });
  };
  const openAnchor = (i, el) => {
    const r = el.getBoundingClientRect();
    setInstant(false);
    setActive(i);
    setMode("anchor");
    setAnchor({ cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  };

  const boxProps = (i) => ({
    "data-tech-box": true,
    onPointerEnter: (e) => {
      if (e.pointerType === "mouse") openHover(i, e);
    },
    onPointerMove: (e) => {
      if (e.pointerType === "mouse" && active === i && mode === "hover")
        setPos({ x: e.clientX, y: e.clientY });
    },
    onPointerLeave: (e) => {
      if (e.pointerType === "mouse" && mode === "hover") setActive(null);
    },
    // Only a keyboard focus opens the card here. When a tap-induced focus
    // opened it too, onClick then saw `active === i` and toggled it straight
    // back shut, so tapping a box did nothing on browsers that focus buttons.
    // :focus-visible is the browser's own read on "this focus came from the
    // keyboard" — a plain ref can't tell, because the focus arrives with the
    // mouse events synthesised after touchend, later than pointerup.
    onFocus: (e) => {
      if (!e.currentTarget.matches(":focus-visible")) return;
      openedByKey.current = true;
      openAnchor(i, e.currentTarget);
    },
    onBlur: () => {
      // A tap-opened card is dismissed by the outside-pointerdown listener, by
      // scroll, or by its close button — closing on blur too would yank it away
      // the moment a finger lands anywhere inside it.
      if (mode === "anchor" && openedByKey.current) setActive(null);
    },
    onClick: (e) => {
      if (hoverable.current) return; // mouse is handled by hover
      openedByKey.current = false;
      if (active === i) setActive(null);
      else openAnchor(i, e.currentTarget);
    },
  });

  // Resolve the fixed popup position from the current mode + measurements.
  let style = null;
  if (active != null) {
    const cw = CARD_W;
    const ch = cardH;
    const { w: vw, h: vh } = win;
    let x;
    let y;
    if (mode === "hover") {
      x = pos.x + 18;
      y = pos.y + 18;
      if (x + cw > vw - 8) x = pos.x - cw - 18;
      if (y + ch > vh - 8) y = pos.y - ch - 18;
    } else if (anchor) {
      x = anchor.cx - cw / 2;
      y = anchor.bottom + 12;
      if (y + ch > vh - 8) y = anchor.top - ch - 12;
    } else {
      x = vw / 2 - cw / 2;
      y = vh / 2 - ch / 2;
    }
    style = {
      left: clamp(x, 8, Math.max(8, vw - cw - 8)),
      top: clamp(y, 8, Math.max(8, vh - ch - 8)),
    };
  }

  const activeTech = active != null ? techs[active] : null;

  const boxClass =
    "flex items-center justify-center rounded-2xl border border-[#DEDCD1]/30 bg-[#262624] text-center font-medium text-[#FAF9F5] outline-none transition-[box-shadow,background-color] duration-200 hover:bg-[#302f2c] hover:ring-1 hover:ring-white/40 focus-visible:ring-2 focus-visible:ring-sky-400";

  return (
    <>
      {/* desktop / tablet: the scaled diagram */}
      <div
        className="relative mx-auto hidden w-full max-w-[1033px] md:block"
        style={{ containerType: "inline-size" }}
      >
        <div className="relative w-full" style={{ aspectRatio: "1033 / 450" }}>
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Connectors />
          </motion.div>

          {techs.map((t, i) => {
            const b = t.box;
            return (
              <motion.button
                key={t.id}
                type="button"
                {...boxProps(i)}
                aria-label={`${t.title} — ${t.role}`}
                // `initial` is what the server writes into the HTML, so it must
                // not depend on the motion preference — only the transition may.
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={
                  reduce
                    ? { opacity: { duration: 0.3 }, y: { duration: 0 } }
                    : { duration: 0.5, delay: 0.1 + i * 0.08, ease: "easeOut" }
                }
                className={`absolute px-4 ${boxClass}`}
                style={{
                  left: `${(b.x / 1033) * 100}%`,
                  top: `${(b.y / 450) * 100}%`,
                  width: `${(b.w / 1033) * 100}%`,
                  height: `${(b.h / 450) * 100}%`,
                  fontSize: "clamp(11px, 1.55cqi, 18px)",
                }}
              >
                <ResponsiveCopy desktop={t.label} mobile={t.labelMobile} />
              </motion.button>
            );
          })}

          {/* one wire rides above the boxes (LINE LIFF ↔ Stripe), per design */}
          <motion.div
            className="pointer-events-none absolute inset-0"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Connectors layer="over" />
          </motion.div>
        </div>
      </div>

      {/* mobile: stacked column */}
      <div className="mx-auto flex w-full max-w-sm flex-col items-stretch md:hidden">
        {techs.map((t, i) => (
          <div key={t.id} className="flex flex-col items-center">
            {i > 0 && (
              <span aria-hidden="true" className="h-5 w-px bg-[#9C9A92]/50" />
            )}
            <motion.button
              type="button"
              {...boxProps(i)}
              aria-label={`${t.title} — ${t.role}`}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={
                reduce
                  ? { opacity: { duration: 0.3 }, y: { duration: 0 } }
                  : { duration: 0.4, delay: i * 0.05, ease: "easeOut" }
              }
              className={`h-14 w-full px-4 text-[15px] ${boxClass}`}
            >
              <ResponsiveCopy desktop={t.label} mobile={t.labelMobile} />
            </motion.button>
          </div>
        ))}
      </div>

      {/* the cursor-following / anchored popup */}
      <AnimatePresence>
        {activeTech && style && (
          <motion.div
            ref={cardRef}
            data-tech-popup
            // a cursor-following card must never eat the mouse; an anchored one
            // has to take the tap that hits its close button
            className={`fixed z-50 ${mode === "anchor" ? "pointer-events-auto" : "pointer-events-none"}`}
            style={style}
            // The anchored card fades without scaling: animating a transform on
            // a backdrop-filter element makes the browser re-snapshot the
            // backdrop every frame, and on mobile the first frames land with no
            // blur at all. The cursor-following card keeps its pop — a desktop
            // GPU keeps up, and it never sits still long enough to notice.
            {...(reduce || mode === "anchor"
              ? {
                  initial: { opacity: 0 },
                  animate: { opacity: 1 },
                  exit: { opacity: 0 },
                }
              : {
                  initial: { opacity: 0, scale: 0.96, y: 6 },
                  animate: { opacity: 1, scale: 1, y: 0 },
                  exit: { opacity: 0, scale: 0.96, y: 6 },
                })}
            transition={{ duration: instant ? 0 : 0.18, ease: "easeOut" }}
          >
            <TechPopup
              tech={activeTech}
              onClose={mode === "anchor" ? () => setActive(null) : undefined}
              closeLabel={closeLabel}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
